import React from 'react';
import {
  FiEye,
  FiFileText,
  FiMail,
  FiMousePointer
} from 'react-icons/fi';

// Type casting for icons to ensure compatibility with React
const FiMailComponent = FiMail as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiFileTextComponent = FiFileText as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEyeComponent = FiEye as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
// const FiArrowUpRightComponent =
//   FiArrowUpRight as unknown as React.ComponentType<{
//     size?: number | string;
//     className?: string;
//   }>;
const FiMousePointerComponent =
  FiMousePointer as unknown as React.ComponentType<{
    size?: number | string;
    className?: string;
  }>;

// Interface defining the structure of data for top cards
interface TopCardsData {
  openRate: {
    value: string; // Percentage or string representation of open rate
    change: string; // Change in open rate (e.g., "+5%")
  };
  clickRate: {
    value: string; // Percentage or string representation of click rate
    change: string; // Change in click rate
  };
  lastOpened: {
    value: string; // Date/time of last email opened
  };
  lastClicked: {
    value: string; // Date/time of last email clicked
  };
  emailsClicked: {
    value: number; // Number of emails clicked
  };
  emailsReceived: {
    value: number; // Number of emails received
  };
  emailsOpened: {
    value: number; // Number of emails opened
  };
  lastActive: {
    value: string; // Date/time of last activity
  };
  engagementScore: {
    value: number; // Numerical score representing engagement
  };
  unsubscribed: {
    value: string; // Status of unsubscription (e.g., "Yes" or "No")
  };
}

// Component to display key recipient metrics in a grid of cards
const RecipientsViewTopCards: React.FC<{ data: TopCardsData }> = ({ data }) => {
  return (
    <>
      {/* First row of cards displaying open rate, click rate, emails received, emails opened, and emails clicked */}
      <div className='grid grid-cols-5 gap-2 mt-6 newtopcards'>
        {/* Open Rate Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Open Rate
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.openRate.value} {/* Display open rate value */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#FFF7E6', // Light yellow background for icon
                borderRadius: '5px',
              }}
            >
              <FiEyeComponent className='text-yellow-500 text-2xl' size={17} />{' '}
              {/* Eye icon for open rate */}
            </span>
          </div>
        </div>

        {/* Click Rate Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Click Rate
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.clickRate.value} {/* Display click rate value */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#ECFDF5', // Light green background for icon
                borderRadius: '5px',
              }}
            >
              <FiMousePointerComponent
                className='text-green-500 text-2xl'
                size={17}
              />{' '}
              {/* Mouse pointer icon for click rate */}
            </span>
          </div>
        </div>

        {/* Emails Received Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Emails Received
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.emailsReceived.value}{' '}
              {/* Display number of emails received */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#FFF1EC', // Light orange background for icon
                borderRadius: '5px',
              }}
            >
              <FiMailComponent className='text-orange-500 text-2xl' size={17} />{' '}
              {/* Mail icon for emails received */}
            </span>
          </div>
        </div>

        {/* Emails Opened Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Emails Opened
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.emailsOpened.value} {/* Display number of emails opened */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#E0F8FB', // Light teal background for icon
                borderRadius: '5px',
              }}
            >
              <FiFileTextComponent
                className='text-teal-500 text-2xl'
                size={17}
              />{' '}
              {/* File text icon for emails opened */}
            </span>
          </div>
        </div>

        {/* Emails Clicked Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Emails Clicked
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.emailsClicked.value}{' '}
              {/* Display number of emails clicked */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#ECFDF5', // Light green background for icon
                borderRadius: '5px',
              }}
            >
              <FiMousePointerComponent
                className='text-green-500 text-2xl'
                size={17}
              />{' '}
              {/* Mouse pointer icon for emails clicked */}
            </span>
          </div>
        </div>
      </div>

      {/* Second row of cards displaying last email opened, last click activity, and last active */}
      <div className='grid grid-cols-3 gap-2 mt-2 newtopcards'>
        {/* Last Email Opened Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Last Email Opened
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.lastOpened.value}{' '}
              {/* Display date/time of last email opened */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#FFF1EC', // Light orange background for icon
                borderRadius: '5px',
              }}
            >
              <FiMailComponent className='text-orange-500 text-2xl' size={17} />{' '}
              {/* Mail icon for last email opened */}
            </span>
          </div>
        </div>

        {/* Last Click Activity Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Last Click Activity
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.lastClicked.value}{' '}
              {/* Display date/time of last click activity */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#E0F8FB', // Light teal background for icon
                borderRadius: '5px',
              }}
            >
              <FiFileTextComponent
                className='text-teal-500 text-2xl'
                size={17}
              />{' '}
              {/* File text icon for last click activity */}
            </span>
          </div>
        </div>

        {/* Last Active Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Last Active
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.lastActive.value} {/* Display date/time of last activity */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#FFF7E6', // Light yellow background for icon
                borderRadius: '5px',
              }}
            >
              <FiEyeComponent className='text-yellow-500 text-2xl' size={17} />{' '}
              {/* Eye icon for last active */}
            </span>
          </div>
        </div>
      </div>

      {/* Third row of cards displaying engagement score and unsubscribed status */}
      <div className='grid grid-cols-2 gap-2 mt-2 newtopcards'>
        {/* Engagement Score Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Engagement Score
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.engagementScore.value} {/* Display engagement score */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#FFF1EC', // Light orange background for icon
                borderRadius: '5px',
              }}
            >
              <FiMailComponent className='text-orange-500 text-2xl' size={17} />{' '}
              {/* Mail icon for engagement score */}
            </span>
          </div>
        </div>

        {/* Unsubscribed Card */}
        <div className='bg-white rounded-md p-4 flex items-center justify-between border border-gray-200 shadow-sm'>
          <div>
            <h3
              className='text-gray-500 text-sm mb-2 font-semibold'
              style={{ color: '#657584', fontSize: '14px' }}
            >
              Unsubscribed
            </h3>
            <p
              className='text-2xl mb-3'
              style={{ color: '#3F5265', fontSize: '28px' }}
            >
              {data.unsubscribed.value} {/* Display unsubscribed status */}
            </p>
          </div>
          <div style={{ height: '-webkit-fill-available' }}>
            <span
              className='flex items-center justify-center mr-2'
              style={{
                width: '30px',
                height: '30px',
                backgroundColor: '#E0F8FB', // Light teal background for icon
                borderRadius: '5px',
              }}
            >
              <FiFileTextComponent
                className='text-teal-500 text-2xl'
                size={17}
              />{' '}
              {/* File text icon for unsubscribed status */}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default RecipientsViewTopCards;
